import axios, { AxiosPromise, AxiosRequestConfig, AxiosResponse } from 'axios';
import { RootState } from 'store/store';
import MyError from './Error';
import { getBlob } from 'utils/file';
import { getUploadFile, releaseUploadFile } from 'utils/pendingUploadFiles';
import { IAnalyticsQuery } from 'store/entity/IAnalyticsQuery';
import {
  getDomainAndPort,
  accessToken,
  accessTokenHeader,
  singleItemAccessToken,
  accessTokenForceQueryParam,
  cdnTimestamp,
} from './Utils';
import {
  IAnalyticsListQuery,
  IAnalyticsListQueryResponse,
} from 'store/entity/IAnalyticsListQuery';
import { IExportResponsesQuery } from 'store/entity/IExportResponsesQuery';
import { IAnalyticResponse } from 'store/entity/IAnalyticResponse';
import {
  ICheckPoint,
  IMultipleAnswerQuestionView,
  IQuestionsView,
  IReportView,
  ISurveyResponseView,
  ISurveyVersionInfo,
  ISurveyViewServer,
} from 'store/survey/surveyTypes';
import cloneDeep from 'lodash/cloneDeep';

const domainAndPort = getDomainAndPort();
const { protocolDomain, port } = domainAndPort;

const baseURL = `${protocolDomain}:${port !== '80' ? port : ''}/man/rest`;
const surveyApi = axios.create({
  baseURL,
  //  timeout: 125000,
});

surveyApi.interceptors.response.use(undefined, (err) => {
  let error;
  if (err.response) {
    error = new MyError(err.response.data, err.response.status);
  } else if (err.request) {
    error = new MyError(err.request, 'request');
  } else {
    error = new MyError(err, 'request');
  }
  return Promise.reject(error);
});

export default surveyApi;

export const getSurvey = (
  surveyToken?: string,
  reportToken?: string,
  ownerToken?: string,
  surveyId?: number
) => {
  return surveyApi.get('/survey/getSurvey', {
    params: {
      survey_token: surveyToken,
      report_token: reportToken,
      owner_token: ownerToken,
      surveyId,
    },
  });
};

export const surveyAddQuestion = (
  question: IQuestionsView | IMultipleAnswerQuestionView,
  surveyToken: string,
  state: RootState
) => {
  delete question.indexOrder;
  return surveyApi.post('/survey/addQuestion', question, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      survey_token: surveyToken,
    },
  });
};

export const surveyUpdateQuestion = (
  question: IQuestionsView | IMultipleAnswerQuestionView,
  surveyToken: string,
  state: RootState
) => {
  const newQuestion = cloneDeep(question);
  delete newQuestion.indexOrder;
  return surveyApi.post('/survey/updateQuestion', newQuestion, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      survey_token: surveyToken,
    },
  });
};

export const reindexSurvey = (surveyToken: string, state: RootState) => {
  return surveyApi.get('/survey/reIndex', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      deleteCurrent: true,
      survey_token: surveyToken,
    },
  });
};

export const surveyUpdate = (
  surveyData: ISurveyViewServer,
  surveyToken: string,
  state: RootState
) => {
  return surveyApi.post('/survey/update', surveyData, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      survey_token: surveyToken,
    },
  });
};
export const addToPhoneValidList = (
  validList: string[],
  surveyToken: string,
  state: RootState
) => {
  return surveyApi.post('/survey/addPhoneValidList', validList, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      survey_token: surveyToken,
    },
  });
};
export const removePhoneValidList = (surveyToken: string, state: RootState) => {
  return surveyApi.post(
    '/survey/removePhoneValidList',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
        survey_token: surveyToken,
      },
    }
  );
};
export const createNewVersion = (
  versionInfo: ISurveyVersionInfo,
  surveyToken: string,
  state: RootState
) => {
  return surveyApi.post('/survey/createNewVersion', versionInfo, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      survey_token: surveyToken,
    },
  });
};
export const setVersion = (
  versionId: string,
  surveyToken: string,
  state: RootState
) => {
  return surveyApi.post(
    '/survey/setVersion',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
        versionId,
        survey_token: surveyToken,
      },
    }
  );
};

export const deleteVersion = (
  versionId: string,
  surveyToken: string,
  state: RootState
) => {
  return surveyApi.post(
    '/survey/removeVersion',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
        versionId,
        survey_token: surveyToken,
      },
    }
  );
};

export const surveyGetAllResponses = (
  state: RootState,
  surveyToken: string,
  start: number,
  count: number,
  instance: any,
  order: string | undefined
): AxiosPromise<ISurveyResponseView[]> => {
  return surveyApi.get('/survey/getAllResponses', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      survey_token: surveyToken,
      start,
      count,
      instance,
      order,
    },
  });
};
export const surveyGetResponse = (
  state: RootState,
  surveyToken: string,
  responseId: number,
  includeDeletedResponse: boolean
): AxiosPromise<ISurveyResponseView> => {
  return surveyApi.get('/survey/getResponse', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      survey_token: surveyToken,
      responseId,
      includeDeletedResponse,
    },
  });
};

export const surveyGetMyResponses = (
  state: RootState,
  surveyToken: string,
  start: number,
  count: number,
  instance: any,
  order: string | undefined
): AxiosPromise<ISurveyResponseView[]> => {
  return surveyApi.get('/survey/getMyResponses', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      survey_token: surveyToken,
      start,
      count,
      instance,
      order,
    },
  });
};

export const removeResponse = (
  state: RootState,
  responseId: number,
  surveyToken: string
) => {
  return surveyApi.get('/survey/removeResponse', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      responseId,
      survey_token: surveyToken,
    },
  });
};

export const updateResponse = (
  state: RootState,
  surveyToken: string,
  response: ISurveyResponseView,
  dontSendNotification: boolean,
  ownerIdToken?: string
) => {
  return surveyApi.post('/survey/updateResponse', response, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      dontSendNotifications: dontSendNotification,
      ...accessToken(state),
      survey_token: surveyToken,
      isDirectAccess: !!singleItemAccessToken(state)?.singleItemAccessToken,
      v2Files: 'true',
      owner_id_token: ownerIdToken,
    },
  });
};

export const submitNewResponse = (
  state: RootState,
  surveyToken: string,
  response: ISurveyResponseView,
  dontSendNotification: boolean,
  ownerIdToken?: string
) => {
  return surveyApi.post(
    '/survey/submitNewResponse',
    {
      ...response,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        dontSendNotifications: dontSendNotification,
        ...accessToken(state),
        survey_token: surveyToken,
        isDirectAccess: !!singleItemAccessToken(state)?.singleItemAccessToken,
        v2Files: 'true',
        owner_id_token: ownerIdToken,
      },
    }
  );
};

export interface ImportResponse {
  inserted: ISurveyResponseView[];
  notInserted: number[];
}

export function importResponsesFromExcel(
  state: RootState,
  file: File,
  surveyToken: string,
  instance: number,
  tags: number[],
  dontSendNotification: boolean,
  companyId: number,
  progressCallBack?: any
) {
  const config: AxiosRequestConfig = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'multipart/form-data',
      ...accessTokenHeader(state),
    },

    onUploadProgress:
      typeof progressCallBack === 'function'
        ? (progressEvent: any) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            progressCallBack(progress);
          }
        : undefined,
    params: {
      ...accessToken(state),
      dontSendNotifications: dontSendNotification,
      ...accessToken(state),
      survey_token: surveyToken,
      tags: tags,
      instance,
      companyId,
    },
    timeout: 0,
  };
  const data = new FormData();
  data.append('file', file, file.name);
  return surveyApi.post(
    'survey/importResponsesFromExcel',
    data,
    config
  ) as Promise<AxiosResponse<ImportResponse>>;
}

export const getReports = (state: RootState, surveyToken: string) => {
  return surveyApi.get('/survey/getReports', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      survey_token: surveyToken,
      ...accessToken(state),
    },
  });
};

export const generateReports = (
  state: RootState,
  questionId: number | undefined,
  config: { [key: string]: string },
  surveyToken: string,
  updateExisting: boolean = false
) => {
  return surveyApi.post('/survey/generateReport', config, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      survey_token: surveyToken,
      questionId,
      updateExisting,
      ...accessToken(state),
    },
  });
};

export const getReportByIds = (reportIds: number[]) => {
  if (reportIds.length > 100)
    return surveyApi.post('/survey/getReportByIds', reportIds);
  return surveyApi.get('/survey/getReportByIds', {
    params: {
      reportIds,
      ...cdnTimestamp(),
    },
  });
};

export const addReport = (
  state: RootState,
  report: IReportView,
  surveyToken?: string,
  reportToken?: string,
  ownerToken?: string
) => {
  return surveyApi.post('/survey/addReport', report, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      survey_token: surveyToken,
      report_token: reportToken,
      owner_token: ownerToken,
      ...accessToken(state),
    },
  });
};

export const updateReport = (
  state: RootState,
  report: IReportView,
  surveyToken?: string,
  reportToken?: string,
  ownerToken?: string
) => {
  return surveyApi.post('/survey/updateReport', report, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      survey_token: surveyToken,
      report_token: reportToken,
      owner_token: ownerToken,
      ...accessToken(state),
    },
  });
};

export const removeReport = (
  state: RootState,
  reportId: number,
  surveyToken?: string,
  reportToken?: string,
  ownerToken?: string
) => {
  return surveyApi.get('/survey/removeReport', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      reportId,
      survey_token: surveyToken,
      report_token: reportToken,
      owner_token: ownerToken,
      ...accessToken(state),
    },
  });
};

export const analyse = (
  state: RootState,
  dataQuery: IAnalyticsQuery,
  surveyToken?: string,
  ownerToken?: string,
  reportToken?: string
): Promise<AxiosResponse<IAnalyticResponse>> => {
  const useMethodGet: boolean = !state.thisUser?.access_token;
  return !useMethodGet
    ? surveyApi.post('/survey/analyse', dataQuery, {
        headers: {
          ...accessTokenHeader(state),
        },
        params: {
          ...accessTokenForceQueryParam(state),
          survey_token: surveyToken,
          owner_token: ownerToken,
          report_token: reportToken,
        },
      })
    : surveyApi.get('/survey/getAnalyse', {
        headers: {
          ...accessTokenHeader(state),
        },
        params: {
          ...accessToken(state),
          survey_token: surveyToken,
          owner_token: ownerToken,
          report_token: reportToken,
          query: JSON.stringify(dataQuery),
        },
      });
};

export const exportResponses = (
  state: RootState,
  dataQuery: IExportResponsesQuery,
  surveyToken?: string,
  ownerToken?: string,
  reportToken?: string
) => {
  return surveyApi.post('/survey/exportResponses', dataQuery, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      survey_token: surveyToken,
      owner_token: ownerToken,
      report_token: reportToken,
    },
    responseType: 'blob',
  });
};

export const canIFill = (
  state: RootState,
  response: ISurveyResponseView,
  surveyToken: string,
  includeDraft?: boolean,
  ownerIdToken?: string
) => {
  return surveyApi.post('/survey/canIFill', response, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      survey_token: surveyToken,
      includeDraft,
      isDirectAccess: !!singleItemAccessToken(state)?.singleItemAccessToken,
      owner_id_token: ownerIdToken,
    },
  });
};
export const getNotifications = (
  state: RootState,
  response: ISurveyResponseView,
  surveyToken: string
) => {
  return surveyApi.post('/survey/getNotificationMessages', response, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      survey_token: surveyToken,
    },
  });
};

const fileUnavailableError = () =>
  new MyError({ message: 'file.unavailable' }, 'request');

const isUnreadableFileError = (err: unknown) => {
  const name = (err as { name?: string })?.name;
  const message = String((err as { message?: string })?.message || '');
  return (
    name === 'NotFoundError' ||
    name === 'NotReadableError' ||
    /could not be read/i.test(message)
  );
};

const assertFileReadable = async (body: Blob) => {
  try {
    // One byte is enough to prove the selected file is still on disk.
    await body.slice(0, 1).arrayBuffer();
  } catch (err) {
    if (isUnreadableFileError(err)) throw fileUnavailableError();
    throw err;
  }
};

const putFile = async (
  url: string,
  body: Blob,
  onProgress?: (percent: number) => void
) => {
  await assertFileReadable(body);

  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader(
      'Content-Type',
      body.type || 'application/octet-stream'
    );
    xhr.upload.onprogress = (event) => {
      const total = event.lengthComputable ? event.total : body.size;
      if (!total) return;
      onProgress?.(Math.min(100, Math.round((event.loaded / total) * 100)));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
        return;
      }
      reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => {
      assertFileReadable(body)
        .then(() => reject(new Error('Upload failed')))
        .catch((err) =>
          reject(isUnreadableFileError(err) ? fileUnavailableError() : err)
        );
    };
    xhr.onabort = () => reject(new Error('Upload aborted'));
    try {
      // Send the original File/Blob. The browser reads it from disk in chunks
      // instead of copying the whole body into the JS heap.
      xhr.send(body);
    } catch (err) {
      if (isUnreadableFileError(err)) {
        reject(fileUnavailableError());
        return;
      }
      reject(err);
    }
  });
};

export const uploadSurveyFiles = async (
  files: {
    [questionId: string]: { url: string; blob: Blob | string };
  },
  onProgress?: (questionId: string, percent: number) => void
) => {
  const entries = Object.entries(files);
  let firstError: unknown;

  // One file at a time so several large answers are not buffered together.
  for (const [questionId, spec] of entries) {
    const stored =
      spec.blob instanceof Blob ? spec.blob : getUploadFile(spec.blob);
    const body = stored ?? (await getBlob(spec.blob as string));
    let lastPercent = -1;
    try {
      await putFile(spec.url, body, (percent) => {
        if (lastPercent === percent) return;
        lastPercent = percent;
        onProgress?.(questionId, percent);
      });
      if (typeof spec.blob === 'string') releaseUploadFile(spec.blob);
    } catch (err) {
      if (!firstError) firstError = err;
    }
  }

  if (firstError) throw firstError;
};

export const addCheckPoint = (
  state: RootState,
  checkPoint: ICheckPoint,
  surveyToken?: string,
  ownerToken?: string
) =>
  surveyApi.post('/survey/addCheckPoint', checkPoint, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      survey_token: surveyToken,
      owner_token: ownerToken,
    },
  });

export const updateCheckPoint = (
  state: RootState,
  checkPoint: ICheckPoint,
  surveyToken?: string,
  ownerToken?: string
) =>
  surveyApi.post('/survey/updateCheckPoint', checkPoint, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      survey_token: surveyToken,
      owner_token: ownerToken,
    },
  });

export const deleteCheckPoint = (
  state: RootState,
  checkPointId: number,
  surveyToken?: string,
  ownerToken?: string
) =>
  surveyApi.get('/survey/deleteCheckPoint', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      checkPointId,
      survey_token: surveyToken,
      owner_token: ownerToken,
    },
  });

export const getCheckPoints = (
  state: RootState,
  surveyId?: number,
  reportId?: number,
  surveyToken?: string,
  ownerToken?: string,
  reportToken?: string
) =>
  surveyApi.get('/survey/getCheckPoints', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      surveyId,
      reportId,
      survey_token: surveyToken,
      owner_token: ownerToken,
      report_token: reportToken,
    },
  });

export const analyseList = (
  state: RootState,
  dataQuery: IAnalyticsListQuery,
  surveyToken?: string,
  ownerToken?: string,
  reportToken?: string,
  from?: number,
  count?: number,
  order?: string,
  questionIdsFilter?: string[]
): AxiosPromise<IAnalyticsListQueryResponse> => {
  return surveyApi.post('/survey/analyseList', dataQuery, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      survey_token: surveyToken,
      owner_token: ownerToken,
      report_token: reportToken,
      from,
      count,
      order,
      questionIdsFilter,
    },
  });
};

export const fillingSurveyQuestionsHandler = (
  state: RootState
): AxiosPromise<any> => {
  return surveyApi.post(
    '/ai-survey-auto-complete',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};
